import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

const Navbar = () => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [language, setLanguage] = useState(i18n.language || 'en');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const toggleDarkMode = () => {
    const newTheme = darkMode ? 'light' : 'dark';
    setDarkMode(!darkMode);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const navItems = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.menu'), to: '/menu' },
    { label: t('nav.contact'), to: '/contact' },
    { label: t('nav.order'), to: '/order' },
  ];

  return (
    <nav className="font-sans bg-pink-100 dark:bg-gray-800 shadow-md px-4 py-3 tracking-wide">
      <div className="max-w-6xl mx-auto flex justify-between items-center">
        {/* Logo */}
        <div className="text-2xl font-extrabold tracking-tight text-pink-700 dark:text-white">
          🍩 Maison Beigne
        </div>

        {/* Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden text-pink-700 dark:text-white text-2xl"
        >
          ☰
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex space-x-4 items-center">
          {navItems.map(({ label, to }) => (
            <Link
              key={to}
              to={to}
              className="uppercase text-sm font-semibold tracking-wide text-pink-700 dark:text-gray-200 hover:text-pink-900"
            >
              {label}
            </Link>
          ))}

          {/* Language Switcher */}
          <div className="flex items-center space-x-1 border border-pink-500 rounded px-2">
            <button
              onClick={() => changeLanguage('en')}
              className={`text-xs font-semibold tracking-wide ${
                language === 'en' ? 'text-pink-700 underline' : 'text-pink-500'
              }`}
            >
              EN
            </button>
            <span className="text-pink-400">/</span>
            <button
              onClick={() => changeLanguage('fr')}
              className={`text-xs font-semibold tracking-wide ${
                language === 'fr' ? 'text-pink-700 underline' : 'text-pink-500'
              }`}
            >
              FR
            </button>
          </div>

          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className="text-xs font-bold tracking-wide px-2 py-1 border border-pink-600 text-pink-600 rounded hover:bg-pink-100 dark:hover:bg-gray-700"
          >
            {darkMode ? '☀️ Light' : '🌙 Dark'}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden mt-3 space-y-2 font-semibold">
          {navItems.map(({ label, to }) => (
            <Link
              key={to}
              to={to}
              className="block text-pink-700 dark:text-white uppercase tracking-wide text-sm"
              onClick={() => setMenuOpen(false)}
            >
              {label}
            </Link>
          ))}
          <div className="flex justify-between px-2 mt-2">
            {/* Lang */}
            <div className="flex space-x-1">
              <button
                onClick={() => changeLanguage('en')}
                className={`text-xs font-semibold ${language === 'en' ? 'underline' : ''}`}
              >
                EN
              </button>
              <button
                onClick={() => changeLanguage('fr')}
                className={`text-xs font-semibold ${language === 'fr' ? 'underline' : ''}`}
              >
                FR
              </button>
            </div>
            {/* Dark mode */}
            <button
              onClick={toggleDarkMode}
              className="text-xs border border-pink-600 px-2 rounded text-pink-600"
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
